import { useState, useEffect } from 'react';
import { Sparkles, Gamepad2 } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher as DesktopLanguageSwitcher } from '@/components/LanguageSwitcher';
import { Logo } from '@/components/Logo';
import { useLanguage } from '@/contexts/LanguageContext';
import { Language, languages } from '@/utils/i18n';
import { useCurrentTheme, withLangAndTheme } from '@/utils/siteSync';

// Flag components for fullscreen menu
function FlagFI() {
  return (
    <svg viewBox="0 0 64 48" className="flag" width="24" height="18" aria-hidden="true">
      <rect width="64" height="48" fill="#fff" />
      <rect x="0" y="18" width="64" height="12" fill="#003580" />
      <rect x="22" y="0" width="12" height="48" fill="#003580" />
    </svg>
  );
}
function FlagEN() {
  return (
    <svg viewBox="0 0 64 48" className="flag" width="24" height="18" aria-hidden="true">
      <clipPath id="en-flag-clip-nav"><rect x="0" y="0" width="64" height="48" rx="2" /></clipPath>
      <g clipPath="url(#en-flag-clip-nav)">
        <path fill="#012169" d="M0 0h64v48H0z" />
        <path stroke="#fff" strokeWidth="8" d="M-8 0l80 60M72 0L-8 60" />
        <path stroke="#C8102E" strokeWidth="4" d="M-8 0l80 60M72 0L-8 60" />
        <path fill="#fff" d="M26 0h12v48H26zM0 18h64v12H0z" />
        <path fill="#C8102E" d="M28 0h8v48h-8zM0 20h64v8H0z" />
      </g>
    </svg>
  );
}
function FlagSV() {
  return (
    <svg viewBox="0 0 64 48" className="flag" width="24" height="18" aria-hidden="true">
      <rect width="64" height="48" fill="#006AA7" />
      <rect x="0" y="20" width="64" height="8" fill="#FECC00" />
      <rect x="18" y="0" width="8" height="48" fill="#FECC00" />
    </svg>
  );
}
const flagComponents: Record<Language, React.FC> = { fi: FlagFI, en: FlagEN, sv: FlagSV };

export function Navigation() {
  const { t, language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open (no position:fixed → no scroll jump on close)
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const theme = useCurrentTheme();

  const navItems = [
    { id: 'home', label: t.nav.home, href: withLangAndTheme('https://haikoinnovations.com/', language, theme) },
    { id: 'services', label: t.nav.services, href: withLangAndTheme('https://haikoinnovations.com/#services', language, theme) },
    { id: 'contact', label: t.nav.contact, href: withLangAndTheme('https://haikoinnovations.com/#contact', language, theme) },
  ];

  return (
    <header className="global-header">
      <nav className={`main-nav ${isScrolled ? 'scrolled' : ''}`} aria-label={t.nav.menu}>
        <div className="main-nav__inner">
          <div className="main-nav__brand">
            <Logo />
          </div>

          <ul className="main-nav__list">
            {navItems.map((item) => (
              <li key={item.id} className="main-nav__item">
                <a href={item.href} className="main-nav__link">
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="main-nav__actions">
            <a
              href={withLangAndTheme('https://haikoinnovations.com/campaign', language, theme)}
              className="special-offer-btn"
            >
              <Sparkles className="w-4 h-4 special-offer-btn__icon" />
              <span className="special-offer-btn__text">{t.specialOffer}</span>
            </a>
            <a
              href={withLangAndTheme('https://haikoinnovations.com/portfolio', language, theme)}
              className="portfolio-button"
              aria-label={t.nav.portfolio}
            >
              <svg className="portfolio-icon" viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
                <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6.5" y="11" width="13" height="9.5" rx="2" />
                  <path d="M12 11V9.2a3.2 3.2 0 0 1 3.2-3.2h8.1a3.2 3.2 0 0 1 3.2 3.2V19" />
                  <path d="M10.6 20.5h10.2A3.2 3.2 0 0 1 24 23.7v2a2.3 2.3 0 0 1-2.3 2.3H8.3A2.3 2.3 0 0 1 6 25.7v-3.1a3.6 3.6 0 0 1 3.6-3.6z" />
                  <path d="M9.5 14.2h6" />
                  <path d="M9.5 17h4" />
                </g>
              </svg>
            </a>
            <button
              className="portfolio-button active"
              aria-label="Haiko Tetris"
              aria-current="page"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Gamepad2 className="w-6 h-6" />
            </button>
            {/* Desktop language switcher kept as import */}
            <div className="lang-switcher-desktop-only">
              <DesktopLanguageSwitcher />
            </div>
            <ThemeToggle />
          </div>

        </div>
      </nav>

      {/* Hamburger button outside nav to avoid stacking context issues */}
      <button
        className={`mobile-menu-btn hamburger ${isMobileMenuOpen ? 'hamburger--open' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className="hamburger__line" />
        <span className="hamburger__line" />
        <span className="hamburger__line" />
      </button>

      {/* Fullscreen mobile menu overlay */}
      <div className={`mobile-menu-fullscreen ${isMobileMenuOpen ? 'mobile-menu-fullscreen--open' : ''}`}>

        <div className="mobile-menu-fullscreen__logo">
          <Logo />
        </div>

        <nav className="mobile-menu-fullscreen__nav">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={item.href}
              className="mobile-menu-fullscreen__link"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item.label}
            </a>
          ))}
          <a
            href={withLangAndTheme('https://haikoinnovations.com/campaign', language, theme)}
            className="special-offer-btn"
            style={{ marginTop: '0.25rem' }}
          >
            <Sparkles className="w-4 h-4 special-offer-btn__icon" />
            <span className="special-offer-btn__text">{t.specialOffer}</span>
          </a>
        </nav>

        <div className="mobile-menu-fullscreen__actions" style={{ flexDirection: 'column', gap: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <a
              href={withLangAndTheme('https://haikoinnovations.com/portfolio', language, theme)}
              className="portfolio-button"
              aria-label={t.nav.portfolio}
            >
              <svg className="portfolio-icon" viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
                <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6.5" y="11" width="13" height="9.5" rx="2" />
                  <path d="M12 11V9.2a3.2 3.2 0 0 1 3.2-3.2h8.1a3.2 3.2 0 0 1 3.2 3.2V19" />
                  <path d="M10.6 20.5h10.2A3.2 3.2 0 0 1 24 23.7v2a2.3 2.3 0 0 1-2.3 2.3H8.3A2.3 2.3 0 0 1 6 25.7v-3.1a3.6 3.6 0 0 1 3.6-3.6z" />
                  <path d="M9.5 14.2h6" />
                  <path d="M9.5 17h4" />
                </g>
              </svg>
            </a>
            <button
              className="portfolio-button active"
              aria-label="Haiko Tetris"
              aria-current="page"
              onClick={() => { setIsMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              <Gamepad2 className="w-6 h-6" />
            </button>
            <span style={{ display: 'inline-flex' }}>
              <ThemeToggle />
            </span>
          </div>
          <div className="mobile-menu-fullscreen__languages">
            {languages.map((lang) => {
              const Flag = flagComponents[lang];
              return (
                <button
                  key={lang}
                  className={`mobile-lang-flag ${language === lang ? 'mobile-lang-flag--active' : ''}`}
                  onClick={() => setLanguage(lang)}
                  aria-label={lang.toUpperCase()}
                >
                  <Flag />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
}

